import * as React from 'react';
import { ChartContainer } from '@mui/x-charts/ChartContainer';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsClipPath } from '@mui/x-charts/ChartsClipPath';
import { ChartsAxisHighlight } from '@mui/x-charts/ChartsAxisHighlight';
import { LineHighlightPlot, LinePlot } from '@mui/x-charts/LineChart';
import {
  dateTickFormat,
  dateFormat,
  elevationFormatter,
  windFormatter,
} from '../utils/format';
import { useHighlightedAxis } from './HighlightedAxisContext';
import NightOverlay from './NightOverlay';
import useId from '@mui/utils/useId';
import { useXScale, useYScale } from '@mui/x-charts';
import * as d3 from 'd3'; // You'll need to install d3-shape for path generation
import WindOverlay from './WindOverlay';

const WindGustArea = ({ data, times, color }) => {
  const xScale = useXScale();
  const yScale = useYScale('left');
  const points = times.map((t, i) => {
    return {
      time: t,
      windSpeed: data.windSpeed[i],
      windGust: data.windGust[i],
    };
  });

  const areaGenerator = d3
    .area()
    .x((d) => xScale(d.time))
    .y0((d) => yScale(d.windSpeed))
    .y1((d) => yScale(d.windGust));

  return <path d={areaGenerator(points)} fill={color} />;
};

function getLabel(label, elevation, elevationLabel) {
  if (elevationLabel === true || elevationLabel === undefined) {
    return `${elevationFormatter.format(elevation)} ft ${label}`;
  }
  return label;
}

export default function WindChart({
  forecast,
  xAxisPosition = 'bottom',
  height = 130,
}) {
  const { highlightedAxis, setHighlightedAxis } = useHighlightedAxis();
  const { elevation, times, data } = forecast;
  const id = useId();
  const clipPathId = `${id}-clip-path`;

  return (
    <ChartContainer
      xAxis={[
        {
          data: times,
          id: 'x-axis',
          domainLimit: 'strict',
          scaleType: 'time',
          valueFormatter: (date, context) =>
            context.location === 'tick'
              ? dateTickFormat(date)
              : dateFormat(date),
          position: xAxisPosition,
        },
      ]}
      yAxis={[
        {
          id: 'left',
          width: 80,
          valueFormatter: (w) => `${windFormatter.format(w)}`,
        },
        {
          id: 'right',
          width: 60,
          position: 'right',
          valueFormatter: (w) => `${windFormatter.format(w)}`,
        },
      ]}
      series={[
        {
          type: 'line',
          label: getLabel('Wind', elevation, false),
          data: data.windSpeed,
          valueFormatter: (w) => `${windFormatter.format(w)} mph`,
          showMark: false,
          color: '#583c69ff',
          yAxisId: 'left',
        },
        {
          type: 'line',
          label: getLabel('Gusts', elevation, false),
          data: data.windGust,
          valueFormatter: (w) => `${windFormatter.format(w)} mph`,
          showMark: false,
          color: '#583c6901',
          yAxisId: 'left',
        },
      ]}
      height={xAxisPosition !== 'none' ? height : height - 20}
      highlightedAxis={highlightedAxis}
      onHighlightedAxisChange={setHighlightedAxis}
      margin={{
        bottom: xAxisPosition === 'bottom' ? 15 : 10,
        top: 15,
      }}
    >
      <g clipPath={`url(#${clipPathId})`}>
        <NightOverlay times={times} />
        <ChartsGrid horizontal vertical />
        <WindGustArea data={data} times={times} color="#583c6930" />
        <LinePlot />
        <WindOverlay forecast={forecast} />
      </g>
      <ChartsClipPath id={clipPathId} />
      <LineHighlightPlot />
      <ChartsAxisHighlight x="line" />
      <ChartsXAxis />
      <ChartsYAxis axisId="left" label="Wind" />
      <ChartsYAxis axisId="right" />
      <ChartsTooltip />
    </ChartContainer>
  );
}
